import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BadgeCheck, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/hooks/use-profile";

const emailSchema = z
  .object({
    email: z.string().trim().email("Enter a valid email address"),
    confirm: z.string().trim(),
  })
  .refine(data => data.email === data.confirm, {
    path: ["confirm"],
    message: "Email addresses don't match",
  });

type EmailFormValues = z.infer<typeof emailSchema>;

export function EmailTab() {
  const { user, emailVerified } = useAuth();
  const { updateEmail } = useProfile();
  const currentEmail = user?.email ?? "";

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "", confirm: "" },
  });

  const onSubmit = (values: EmailFormValues) => {
    if (values.email.toLowerCase() === currentEmail.toLowerCase()) {
      form.setError("email", { message: "That is already your current email." });
      return;
    }
    updateEmail.mutate(values.email, { onSuccess: () => form.reset({ email: "", confirm: "" }) });
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-lg">Email address</CardTitle>
        <CardDescription>This is the email you use to sign in and receive notifications.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label>Current email</Label>
              <Input value={currentEmail} disabled />
              {emailVerified && (
                <Badge variant="secondary" className="bg-success/15 text-success hover:bg-success/15">
                  <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                  Verified
                </Badge>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm new email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={updateEmail.isPending}>
                {updateEmail.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Update email
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
